import { isNumber, isString } from 'lodash';
import moment from 'moment';

/**
 * Formats milliseconds into a h:mm:ss string. Where hours may be omitted if 0 or always included.
 * @param timespan Value in milliseconds.
 * @param includeHours whether or not to include hours in the output. If false, hours is only omitted when it is a 0-value. If includeSeconds is false, this param will be ignored and hours will always be included.
 * @param includeSeconds whether or not to include seconds in the output.
 */
export function formatTimespanShort(
  timespan: number,
  includeHours = false,
  includeSeconds = true,
): string {
  const timespanNum: number = isString(timespan) ? +timespan : timespan;

  if (isNaN(timespanNum) || !isNumber(timespanNum) || timespanNum < 0) {
    return '';
  }

  const formattedTimeSpan: string = formatTimespan(timespanNum, {
    noDays: true,
    noHours: !includeHours && includeSeconds,
    padFields: true,
    padHours: false,
    noSeconds: !includeSeconds,
    noMilliseconds: true,
  });

  return formattedTimeSpan;
}

export function formatTimespanMs(timespan: number): string {
  const timespanNum: number = isString(timespan) ? +timespan : timespan;

  if (isNaN(timespanNum) || !isNumber(timespanNum) || timespanNum < 0) {
    return '';
  }

  const formattedTimeSpan: string = formatTimespan(timespanNum, {
    noDays: true,
    noHours: true,
    noSeconds: false,
    padSeconds: true,
    includeMilliseconds: true,
    noMilliseconds: false,
  });

  return formattedTimeSpan;
}

/**
 * Format a timespan as a string. The format is s.fff
 */
export function formatTimespanShortMs(timespan: number): string {
  const timespanNum: number = isString(timespan) ? +timespan : timespan;

  if (isNaN(timespanNum) || !isNumber(timespanNum) || timespanNum < 0) {
    return '';
  }

  const formattedTimeSpan: string = formatTimespan(timespanNum, {
    noDays: true,
    noHours: true,
    noMinutes: false,
    padFields: true,
    noSeconds: false,
    includeMilliseconds: true,
    noMilliseconds: false,
  });

  return formattedTimeSpan;
}

/**
 * formats timespan as d.hh:mm:ss.ffff
 * @param options IFormatTimespanOptions
 */
export function formatTimespan(timespan: number, opts?: any): string {
  const pad2IfOpt = (value: number | string): number | string => {
    return opts.padFields ? pad(value, 2) : value;
  };
  const timeInfo = getTimeInfo(timespan);
  opts = opts || {};

  if (!timeInfo || (timeInfo.negative && !opts.allowNegative)) {
    return '';
  }
  const output = [];

  if (timeInfo.negative) {
    output.push('-');
  }

  const includeDays: boolean = timeInfo.asDays > 0 && !opts.noDays;

  if (includeDays) {
    output.push(timeInfo.asDays, '.');
  }

  if (timeInfo.asHours || !opts.noHours) {
    const hh = includeDays ? timeInfo.hours : timeInfo.asHours;
    const padHours = opts.padHours !== undefined ? opts.padHours : opts.padFields;

    output.push(padHours ? pad(hh, 2) : hh, ':');
  }

  if (timeInfo.minutes > 0 && !opts.noMinutes) {
	output.push(pad2IfOpt(timeInfo.minutes));
    if (!opts.noSeconds) {
      output.push(':');
    }
  }

  if (!opts.noSeconds) {
    output.push(pad(timeInfo.seconds, 2));
    if (opts.includeMilliseconds || (timeInfo.milliseconds && !opts.noMilliseconds)) {
      output.push(opts.decimalSeparator || '.', pad(timeInfo.milliseconds, 3));
    }
  }

  return output.join('');
}

function getTimeInfo(ms: number) {
  ms = Math.floor(+ms);
  if (isNaN(ms) || !isNumber(ms)) {
    return 0;
  }

  let neg = false;
  if (ms < 0) {
    neg = true;
    ms *= -1;
  }

  const duration: moment.Duration = moment.duration(ms);

  return {
    asDays: Math.floor(duration.asDays()),
    asHours: Math.floor(duration.asHours()),
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
    milliseconds: duration.milliseconds(),
    negative: neg,
  };
}

function pad(input: number | string, targetLength: number): string {
  return input.toString().padStart(targetLength, '0');
}
